<template>
  <div class="automatic-scoring">
    <template v-if="readOnly">
      <el-form label-suffix="：" inline>
        <el-row :gutter="40">
          <el-col :xs="12" :sm="8" :xl="6">
            <el-form-item label="价格分总分设置" prop="scoreSum">
              <span>{{ form.scoreSum }}</span>
            </el-form-item>
          </el-col>
          <el-col :xs="12" :sm="8" :xl="6">
            <el-form-item label="评分方案">
              <span>自动算分</span>
            </el-form-item>
          </el-col>
          <el-col :xs="12" :sm="8" :xl="6">
            <el-form-item label="评标基准价设置" prop="benchPriceType">
              <span>
                {{
                  bidReviewBasePriceSettingDict.translate(form.benchPriceType)
                    .label
                }}
              </span>
            </el-form-item>
          </el-col>
          <el-col :xs="12" :sm="8" :xl="6">
            <el-form-item
              v-if="needSetPercentage"
              label="百分比设置（%）"
              prop="percentage"
            >
              <span>{{ form.percentage }}</span>
            </el-form-item>
          </el-col>
          <el-col :xs="12" :sm="12" :xl="12">
            <el-form-item
              v-if="needSetPercentage"
              label="扣分机制设置"
              prop="highUnitLessScore"
            >
              <div>
                投标报价每高于评标基准价的1%扣（分）
                {{ form.highUnitLessScore }}
              </div>
              <div>
                投标报价每低于评标基准价的1%扣（分）
                {{ form.lowUnitLessScore }}
              </div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </template>
    <el-form
      v-else
      ref="form"
      :model="form"
      :rules="rules"
      label-suffix="："
      inline
    >
      <el-form-item label="价格分总分设置" prop="scoreSum">
        <el-input-number
          v-model="form.scoreSum"
          style="width: 180px"
          placeholder="请填写总分值"
          :controls="false"
          :min="0"
          :max="100"
          :precision="2"
        />
      </el-form-item>
      <el-form-item label="评标基准价设置" prop="benchPriceType">
        <el-select
          v-model="form.benchPriceType"
          placeholder="请选择"
          style="width: 280px"
        >
          <el-option
            v-for="item in bidReviewBasePriceSettingDict.array"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item
        v-if="needSetPercentage"
        label="百分比设置（%）"
        prop="percentage"
      >
        <el-input-number
          v-model="form.percentage"
          placeholder="请填写"
          :controls="false"
          :max="100"
          :min="0"
          :precision="2"
        />
      </el-form-item>
      <el-form-item
        v-if="needSetPercentage"
        label="扣分机制设置"
        prop="highUnitLessScore"
      >
        <div class="m-b-16">
          投标报价每高于评标基准价的1%扣（分）
          <el-input-number
            v-model="form.highUnitLessScore"
            placeholder="请填写"
            :controls="false"
            :precision="decimalPoint"
            :min="0"
            :max="Number(form.scoreSum) || 100"
          />
        </div>
        <div>
          投标报价每低于评标基准价的1%扣（分）
          <el-input-number
            v-model="form.lowUnitLessScore"
            placeholder="请填写"
            :controls="false"
            :precision="decimalPoint"
            :min="0"
            :max="Number(form.scoreSum) || 100"
          />
        </div>
      </el-form-item>
    </el-form>
    <div v-if="form.benchPriceType" class="formula">
      <div class="formula-label">计算公式：</div>
      <!-- 有效报价最高价的计算公式 -->
      <div
        v-if="
          form.benchPriceType === bidReviewBasePriceSettingDict.HIGHEST.value
        "
        class="formula-value"
      >
        最终得分 = (投标报价/评标基准价) x
        <span class="text-danger">{{ form.scoreSum || '价格分总分' }}</span>
      </div>
      <!-- 有效报价最低价的计算公式 -->
      <div
        v-if="
          form.benchPriceType === bidReviewBasePriceSettingDict.LOWEST.value
        "
        class="formula-value"
      >
        最终得分 = (评标基准价/投标报价) x
        <span class="text-danger">{{ form.scoreSum || '价格分总分' }}</span>
      </div>
      <!-- 有效报价平均值*百分比的计算公式 -->
      <!-- 去掉最高和最低报价的平均值*百分比 -->
      <div
        v-if="
          form.benchPriceType === bidReviewBasePriceSettingDict.AVERAGE.value ||
            form.benchPriceType ===
            bidReviewBasePriceSettingDict.WITHOUT_HIGHEST_AND_LOWEST.value
        "
        class="formula-value"
      >
        <div class="m-b-10">
          报价高于基准得分 =
          <span class="text-danger">{{ form.scoreSum || '价格分总分' }}</span>
          - (投标报价/评标基准价 - 1) x 100 x
          <span class="text-danger">
            {{ form.highUnitLessScore || '设置的扣分' }}
          </span>
        </div>
        <div>
          报价低于于基准得分 =
          <span class="text-danger">{{ form.scoreSum || '价格分总分' }}</span>
          - (1 - 投标报价/评标基准价) x 100 x
          <span class="text-danger">
            {{ form.lowUnitLessScore || '设置的扣分' }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { bidReviewBasePriceSettingDict } from '@/dictionary/project/kpb-project'
import { requiredRule } from '@/utils/form-validate'
import { cloneDeep, isEqual } from 'lodash'

/**
 * @description 自动算分配置和显示
 */
export default {
  name: 'AutomaticScoring',

  props: {
    // 已设置总分值
    alreadyScore: {
      type: Number,
      default: 0
    },

    // 自动算分设置
    autoScoring: {
      type: Object,
      default: () => {}
    },

    // 评审总分
    scoreSum: {
      type: [Number, String],
      default: ''
    },

    readOnly: {
      type: Boolean,
      default: false
    },

    // 算分精度
    decimalPoint: {
      type: Number,
      default: 4
    }
  },

  data() {
    return {
      bidReviewBasePriceSettingDict,

      form: {
        // 总分
        scoreSum: undefined,
        // 评标基准价设置
        benchPriceType: '',
        // 百分比设置
        percentage: 100,
        // 高于基准价扣分
        highUnitLessScore: undefined,
        // 低于基准价扣分
        lowUnitLessScore: undefined
      },
      rules: {
        scoreSum: [
          requiredRule('请填写总分值'),
          { validator: this.validateScoreSum, trigger: 'blur' }
        ],
        benchPriceType: requiredRule('请选择', '', ['blur', 'change']),
        percentage: requiredRule('请填写'),
        highUnitLessScore: requiredRule('请填写'),
        lowUnitLessScore: requiredRule('请填写')
      }
    }
  },

  computed: {
    // 需要设置百分比
    needSetPercentage() {
      const entry =
        bidReviewBasePriceSettingDict.translate(this.form.benchPriceType) || {}
      return entry.needPercentage
    }
  },

  watch: {
    autoScoring: {
      handler(val) {
        if (val) {
          this.form = Object.assign({}, this.form, val)
        }
      },
      immediate: true,
      deep: true
    },

    scoreSum: {
      handler(val) {
        if (val) this.form.scoreSum = val
      },
      immediate: true
    },

    form: {
      handler(val) {
        if (val && !isEqual(val, this.autoScoring)) {
          this.updateForm()
        }
      },
      deep: true
    }
  },

  methods: {
    /**
     * 限制总分值只能输入正整数
     */
    changeScoreSumInput() {
      const pattern = /^[1-9][0-9]*$/
      if (!pattern.test(this.form.scoreSum)) {
        this.form.scoreSum = ''
      }
    },

    validateScoreSum(rule, value, callback) {
      let tempAlreadyScore = this.alreadyScore
      if (!this.scoreSum) {
        // 如果当前评审办法总分没有从外部传入值，则需要添加表单中的总分值
        tempAlreadyScore = tempAlreadyScore + this.form.scoreSum
      }
      if (tempAlreadyScore > 100) {
        callback(new Error('各评审办法的总分值不能超过100'))
      } else {
        callback()
      }
    },

    clearValidate() {
      this.$refs.form.clearValidate()
    },

    validForm() {
      return new Promise((resolve, reject) => {
        this.$refs.form.validate(valid => {
          if (!valid) {
            this.$message.error('自动算分设置有误，请重新设置')
            return
          }
          resolve(valid)
        })
      })
    },

    updateForm() {
      this.$emit('updateScoreForm', cloneDeep(this.form))
    }
  }
}
</script>

<style lang="scss" scoped>
.formula {
  padding: 0 24px 24px 24px;
  font-size: 12px;
  color: $second-text-color;
  display: flex;
  align-items: stretch;
}
</style>
